import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { GraphDotnetService, graphql, Maybe, MediaclipMutationInvalidateExternalPhotoCacheArgs, MediaclipMutationReEncodePhotoArgs, Photo } from 'app/shared/graph';

@Injectable({
    providedIn: 'root'
})
export class PhotosService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    reEncodePhoto(urn: string): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationReEncodePhotoArgs>(graphql`
                mutation reEncodePhoto($urn: String!) {
                    reEncodePhoto(urn: $urn)
                }
            `, { urn }
        ).pipe(map(x => x.reEncodePhoto));
    }

    invalidateExternalPhotoCache(photoUrn: string): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationInvalidateExternalPhotoCacheArgs>(graphql`
                mutation invalidateExternalPhotoCache($urn: String!) {
                    invalidateExternalPhotoCache(urn: $urn)
                }
            `, { urn: photoUrn }
        ).pipe(map(x => x.invalidateExternalPhotoCache));
    }

    getPhotoInfoForUser(hubUserId: string, photoUrn: string): Observable<Maybe<Photo> | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProject($hubUserId: UUID!, $photoUrn: String!) {
                    user(id: $hubUserId) {
                        photo(urn: $photoUrn) {
                            urn
                            dateUploadedUtc
                            dateTakenUtc
                            width
                            height
                            thumbnail
                            print
                            isCached
                            isCacheable
                            ownerId
                            openLogsUrl
                            storeId
                            store {
                                id
                                subscriptionId
                            }
                            availableBackupVersions {
                                url
                                version
                            }
                            versions {
                                width
                                height
                                url
                            }
                            metadata {
                                name
                                value
                            }
                            autoCrops {
                                x
                                y
                                width
                                height
                            }
                        }
                    }
                }
            `,
            { hubUserId, photoUrn }
        ).pipe(map((result) => result.user?.photo));
    }

    getPhotoInfo(photoUrn: string): Observable<Maybe<Photo> | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProject($photoUrn: String!) {
                    photo(urn: $photoUrn) {
                        urn
                        dateUploadedUtc
                        dateTakenUtc
                        width
                        height
                        thumbnail
                        print
                        isCached
                        isCacheable
                        ownerId
                        openLogsUrl
                        storeId
                        store {
                            id
                            subscriptionId
                        }
                        availableBackupVersions {
                            url
                            version
                        }
                        versions {
                            width
                            height
                            url
                        }
                        metadata {
                            name
                            value
                        }
                        autoCrops {
                            x
                            y
                            width
                            height
                        }
                    }
                }
            `,
            { photoUrn }
        ).pipe(map((result) => result.photo));
    }

    replacePhotoImage(photoUrn: string, version: string, file: File): Observable<any> {
        return this.graphDotnetService.upload(`/tools/replace-upload-photo-image?photoUrn=${encodeURIComponent(photoUrn)}&version=${encodeURIComponent(version)}`, file)
    }
}
